import type {
  ChatTurn,
  CompletionChunk,
  CompletionRequest,
  CompletionResult,
  LLMProvider,
  PreparedRequest,
  ProviderProfile,
  TransportResponse,
} from "@roqeet/core";
import { parseSSE } from "../sse.js";
import { getExactTokenCounter } from "../../tokenizer/index.js";

/**
 * OpenAI 계열 Provider 어댑터 — 결정 #7. OpenAI Chat Completions API 형식을
 * 따르는 모든 엔드포인트를 커버한다:
 *  - "openai": api.openai.com
 *  - "openrouter": openrouter.ai (동일 형식 + 추가 헤더)
 *  - "openai-compatible": 사용자 지정 baseUrl(로컬 Ollama/LM Studio, vLLM 등)
 *
 * 이 한 어댑터가 가장 넓은 범위를 커버하므로 ROI가 가장 높다(메뉴얼 로드맵 참고).
 */
export class OpenAiProvider implements LLMProvider {
  constructor(readonly kind: ProviderProfile["kind"] = "openai") {}

  private baseUrl(profile: ProviderProfile): string {
    if (profile.baseUrl) return profile.baseUrl.replace(/\/+$/, "");
    if (profile.kind === "openrouter") return "https://openrouter.ai/api/v1";
    return "https://api.openai.com/v1";
  }

  prepare(req: CompletionRequest, profile: ProviderProfile, apiKey?: string): PreparedRequest {
    const headers: Record<string, string> = {
      "content-type": "application/json",
      ...(profile.extraHeaders ?? {}),
    };
    if (apiKey) headers["authorization"] = `Bearer ${apiKey}`;
    // OpenRouter 권장 헤더(랭킹/식별용, 선택).
    if (profile.kind === "openrouter") {
      headers["http-referer"] = headers["http-referer"] ?? "https://roqeet.com";
      headers["x-title"] = headers["x-title"] ?? "RoQeet Launcher";
    }

    const body: Record<string, unknown> = {
      model: req.model,
      messages: req.messages.map((m) => toOpenAiMessage(m)),
      stream: req.stream,
      ...mapSampling(req.params, profile.kind, req.model),
    };
    if (req.stream && body["stream_options"] === undefined) {
      body["stream_options"] = { include_usage: true };
    }

    const prepared: PreparedRequest = {
      url: `${this.baseUrl(profile)}/chat/completions`,
      method: "POST",
      headers,
      body: JSON.stringify(body),
    };
    if (req.signal) prepared.signal = req.signal;
    return prepared;
  }

  async *parseStream(res: TransportResponse): AsyncIterable<CompletionChunk> {
    if (!res.stream) {
      // 스트림이 없으면 전체 본문을 1회 결과로 방출(폴백).
      const result = await this.parseResult(res);
      if (result.text) yield { delta: result.text };
      return;
    }
    for await (const payload of parseSSE(res.stream)) {
      if (payload === "[DONE]") return;
      let json: OpenAiStreamChunk;
      try {
        json = JSON.parse(payload) as OpenAiStreamChunk;
      } catch {
        continue;
      }
      const choice = json.choices?.[0];
      const delta = choice?.delta;
      const chunk: CompletionChunk = { delta: delta?.content ?? "" };
      // 일부 호환 엔드포인트(추론 모델)는 reasoning_content를 분리해 보낸다.
      const reasoning = delta?.reasoning_content ?? delta?.reasoning;
      if (reasoning) chunk.reasoningDelta = reasoning;
      const usage = mapUsage(json.usage);
      if (usage) chunk.usage = usage;
      if (chunk.delta || chunk.reasoningDelta || chunk.usage) yield chunk;
    }
  }

  async parseResult(res: TransportResponse): Promise<CompletionResult> {
    const raw = (await res.text?.()) ?? "";
    if (!res.ok) {
      throw new Error(`AI 요청 실패 (HTTP ${res.status}): ${raw.slice(0, 500)}`);
    }
    const json = JSON.parse(raw) as OpenAiResult;
    const choice = json.choices?.[0];
    const result: CompletionResult = {
      text: choice?.message?.content ?? "",
      finishReason: mapFinish(choice?.finish_reason),
    };
    if (json.model) result.model = json.model;
    if (json.usage) {
      result.usage = {
        promptTokens: json.usage.prompt_tokens,
        completionTokens: json.usage.completion_tokens,
        reasoningTokens: json.usage.completion_tokens_details?.reasoning_tokens,
        totalTokens: json.usage.total_tokens,
      };
    }
    return result;
  }

  async countTokens(text: string, model: string): Promise<number> {
    // 정확 토크나이저(tiktoken)를 lazy-load(결정 #12). OpenAI 계열은 공식 인코딩이
    // 있어 정확하다. 로드 실패 시 모듈 내부에서 휴리스틱으로 폴백한다.
    const count = await getExactTokenCounter(model);
    return count(text);
  }

  async listModels(profile: ProviderProfile, apiKey?: string): Promise<string[]> {
    const headers: Record<string, string> = { ...(profile.extraHeaders ?? {}) };
    if (apiKey) headers["authorization"] = `Bearer ${apiKey}`;
    const res = await fetch(`${this.baseUrl(profile)}/models`, { headers });
    if (!res.ok) return [];
    const json = (await res.json()) as { data?: { id: string }[] };
    return (json.data ?? []).map((m) => m.id).sort();
  }
}

function toOpenAiMessage(m: ChatTurn): { role: string; content: string; name?: string } {
  const out: { role: string; content: string; name?: string } = {
    role: m.role,
    content: m.content,
  };
  if (m.name) out.name = sanitizeName(m.name);
  return out;
}

/** OpenAI name 필드는 공백/특수문자를 제한 → 안전화. */
function sanitizeName(name: string): string {
  return name.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 64);
}

function mapSampling(
  p: CompletionRequest["params"],
  kind: ProviderProfile["kind"],
  model: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.temperature != null) out["temperature"] = p.temperature;
  if (p.topP != null) out["top_p"] = p.topP;
  if (p.maxTokens != null) out["max_tokens"] = p.maxTokens;
  if (p.frequencyPenalty != null) out["frequency_penalty"] = p.frequencyPenalty;
  if (p.presencePenalty != null) out["presence_penalty"] = p.presencePenalty;
  if (p.stop && p.stop.length) out["stop"] = p.stop;
  if (p.reasoningLevel === "off") {
    if (kind === "openai" && /^gpt-5(?:\.|$|-)/i.test(model)) {
      out["reasoning_effort"] = "none";
    }
  } else if (p.reasoningLevel && p.reasoningLevel !== "auto") {
    if (kind === "openrouter") {
      out["reasoning"] = { effort: p.reasoningLevel };
      out["include_reasoning"] = true;
    } else {
      out["reasoning_effort"] = p.reasoningLevel;
    }
  }
  if (p.extra) Object.assign(out, p.extra);
  return out;
}

function mapUsage(usage?: OpenAiUsage): CompletionResult["usage"] | undefined {
  if (!usage) return undefined;
  return {
    promptTokens: usage.prompt_tokens,
    completionTokens: usage.completion_tokens,
    reasoningTokens: usage.completion_tokens_details?.reasoning_tokens,
    totalTokens: usage.total_tokens,
  };
}

function mapFinish(reason?: string): CompletionResult["finishReason"] {
  switch (reason) {
    case "stop":
      return "stop";
    case "length":
      return "length";
    case "content_filter":
      return "content-filter";
    default:
      return reason ? "stop" : undefined;
  }
}

interface OpenAiStreamChunk {
  choices?: {
    delta?: { content?: string; reasoning_content?: string; reasoning?: string };
    finish_reason?: string;
  }[];
  usage?: OpenAiUsage;
}

interface OpenAiResult {
  model?: string;
  choices?: { message?: { content?: string }; finish_reason?: string }[];
  usage?: OpenAiUsage;
}

interface OpenAiUsage {
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  completion_tokens_details?: { reasoning_tokens?: number };
}
